import { useContext, useEffect, useState } from 'react';
import {
  Page,
  BackLink,
  Title,
  Subtitle,
  Card,
  CardTitle,
  Row,
  RowText,
  RowLabel,
  RowHint,
  Toggle,
  CredentialRow,
  RevokeHint,
  RevokeButton,
  StatusMessage,
  ErrorMessage,
} from './Settings.styles';
import { UserContext } from '../../context/UserContextBase';

const Settings = () => {
  const { credentialId, refreshPortalSession } = useContext(UserContext);
  const [emailNotifs, setEmailNotifs] = useState(true);
  const [deadlineReminders, setDeadlineReminders] = useState(true);
  const [revokedMessage, setRevokedMessage] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);

  useEffect(() => {
    if (credentialId) {
      setRevokedMessage(null);
      setRevokeError(null);
    }
  }, [credentialId]);

  const handleRevoke = async () => {
    if (!credentialId || revoking) return;
    setRevokeError(null);
    setRevoking(true);
    try {
      const res = await fetch('/api/issue-student-id/revoke', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          credentialId: credentialId,
          reason: 'User revoked digital student ID from university portal settings',
        }),
      });
      if (res.status === 204) {
        await refreshPortalSession();
        setRevokedMessage(
          'Your digital student ID credential has been revoked with the issuer. Open Get Digital Student ID again to request a new offer.',
        );
        return;
      }
      let msg = `Revocation failed (${res.status})`;
      try {
        const j = (await res.json()) as { message?: string };
        if (j?.message) {
          msg = j.message;
        }
      } catch {
        /* ignore */
      }
      setRevokeError(msg);
    } catch (e) {
      setRevokeError(e instanceof Error ? e.message : 'Network error');
    } finally {
      setRevoking(false);
    }
  };

  return (
    <Page>
      <BackLink to="/home">← Back to portal</BackLink>
      <Title>Settings</Title>
      <Subtitle>Manage notifications and your digital credentials for this demo portal.</Subtitle>

      <Card>
        <CardTitle>Notifications</CardTitle>
        <Row>
          <RowText>
            <RowLabel>Email notifications</RowLabel>
            <RowHint>Course updates and university announcements (demo toggle).</RowHint>
          </RowText>
          <Toggle checked={emailNotifs} onChange={() => setEmailNotifs((v) => !v)} />
        </Row>
        <Row>
          <RowText>
            <RowLabel>Assignment deadline reminders</RowLabel>
            <RowHint>Digest of upcoming due dates (demo toggle).</RowHint>
          </RowText>
          <Toggle checked={deadlineReminders} onChange={() => setDeadlineReminders((v) => !v)} />
        </Row>
      </Card>

      <Card>
        <CardTitle>Digital credentials</CardTitle>
        <CredentialRow>
          <RevokeHint>
            After you open Get Digital Student ID and the offer is created, you can revoke the credential with the
            issuer. The button stays disabled until a credentialId is returned from that flow.
          </RevokeHint>
          <RevokeButton
            type="button"
            disabled={!credentialId || revoking}
            onClick={() => void handleRevoke()}
          >
            {revoking ? 'Revoking…' : 'Revoke student ID credential'}
          </RevokeButton>
          {revokeError && <ErrorMessage role="alert">{revokeError}</ErrorMessage>}
          {revokedMessage && <StatusMessage role="status">{revokedMessage}</StatusMessage>}
        </CredentialRow>
      </Card>
    </Page>
  );
};

export default Settings;
